/**
 * Waiting room ("lobby") shared contract.
 *
 * Anyone holding the meeting link can open it and type a name, but typing a
 * name only gets them a place in a queue — a moderator has to admit them before
 * a room token is ever issued.
 *
 * WHERE THE QUEUE LIVES: on LiveKit ROOM METADATA, alongside `RoomMediaPolicy`.
 * Same reasoning as the media policy (see mediaPolicy.ts):
 *
 *   - It is pushed to every moderator already in the room via
 *     `RoomMetadataChanged`, so nobody has to poll for knocks.
 *   - It is delivered in the JoinResponse, so a moderator who joins AFTER
 *     someone knocked still sees the pending request. A data packet cannot do
 *     that — packets never replay for someone who was not in the room.
 *   - It survives a Next.js restart and does not assume a single server
 *     instance, which an in-memory store would.
 *
 * The person knocking is NOT in the room (that is the whole point), so their
 * side is the one place that has to poll — `/api/lobby/status`.
 *
 * NOTE: this file is mirrored in the Beam Live mobile app at
 * `mobile/src/helpers/lobby.ts` (separate repo, no shared package). Any change
 * here must be mirrored there or the two clients will disagree about who is
 * waiting.
 */

import { parseMetadataObject } from './mediaPolicy';

/** Key under which the queue is stored on room metadata. */
export const LOBBY_METADATA_KEY = 'lobby';

export type LobbyStatus = 'pending' | 'approved' | 'denied';

export interface LobbyEntry {
  /**
   * The ticket. Handed to the person knocking and exchanged for a room token
   * once (and only once) a moderator has approved it — so it must be
   * unguessable, not a counter.
   */
  id: string;
  /** Display name they typed. Also the name their token is minted with. */
  name: string;
  requestedAt: number;
  status: LobbyStatus;
  decidedAt?: number;
}

/**
 * A pending request outlives a moderator stepping away for a coffee, but not a
 * whole meeting — a stale knock in the drawer is worse than no knock, because
 * admitting it does nothing visible.
 */
export const PENDING_TTL_MS = 10 * 60 * 1000;

/**
 * A decided entry only has to live long enough for the waiting client's next
 * poll to read the verdict, plus enough slack to survive a page reload while
 * they walk to their desk.
 */
export const DECIDED_TTL_MS = 5 * 60 * 1000;

/** Hard cap so a flood of knocks can't grow room metadata without bound. */
export const MAX_LOBBY_ENTRIES = 50;

export const MAX_LOBBY_NAME_LENGTH = 64;

/** How often a waiting client asks whether it has been let in. */
export const LOBBY_POLL_INTERVAL_MS = 2000;

export const sanitizeLobbyName = (name: string): string =>
  name.trim().replace(/\s+/g, ' ').slice(0, MAX_LOBBY_NAME_LENGTH);

const isLobbyEntry = (value: any): value is LobbyEntry =>
  !!value &&
  typeof value === 'object' &&
  typeof value.id === 'string' &&
  value.id.length > 0 &&
  typeof value.name === 'string' &&
  typeof value.requestedAt === 'number' &&
  (value.status === 'pending' || value.status === 'approved' || value.status === 'denied');

/** Every queue entry on this room's metadata, oldest request first. */
export const parseLobby = (metadata?: string | null): LobbyEntry[] => {
  const raw = parseMetadataObject(metadata)[LOBBY_METADATA_KEY];
  if (!Array.isArray(raw)) return [];
  return raw.filter(isLobbyEntry).sort((a, b) => a.requestedAt - b.requestedAt);
};

/** Just the people still waiting, in the order they asked. */
export const pendingLobbyEntries = (entries: LobbyEntry[]): LobbyEntry[] =>
  entries.filter((entry) => entry.status === 'pending');

/**
 * Drop entries nobody is waiting on any more. Run on every write, so the queue
 * is garbage-collected by traffic rather than by a timer we would have to own.
 */
export const pruneLobby = (entries: LobbyEntry[], now = Date.now()): LobbyEntry[] => {
  const kept = entries.filter((entry) => {
    if (entry.status === 'pending') return now - entry.requestedAt < PENDING_TTL_MS;
    return now - (entry.decidedAt ?? entry.requestedAt) < DECIDED_TTL_MS;
  });
  // Oldest first, so trimming to the cap discards the stalest requests rather
  // than the ones a moderator is most likely about to act on.
  return kept.slice(-MAX_LOBBY_ENTRIES);
};

/**
 * Write the queue back, preserving the media policy and anything else already
 * on room metadata.
 */
export const withLobby = (
  metadata: string | null | undefined,
  entries: LobbyEntry[],
): string => {
  const base = parseMetadataObject(metadata);
  if (entries.length === 0) {
    delete base[LOBBY_METADATA_KEY];
  } else {
    base[LOBBY_METADATA_KEY] = entries;
  }
  return JSON.stringify(base);
};

export const findLobbyEntry = (
  entries: LobbyEntry[],
  ticket: string,
): LobbyEntry | undefined => entries.find((entry) => entry.id === ticket);

/**
 * What a waiting client is told. `expired` covers both "this ticket was pruned"
 * and "this room no longer exists" — from the waiting side they are the same
 * thing: the request is gone and has to be made again.
 */
export type LobbyTicketStatus = LobbyStatus | 'expired';

/** Shape returned by /api/lobby/knock and /api/lobby/status. */
export interface LobbyTicketResponse {
  ticket: string;
  status: LobbyTicketStatus;
  name?: string;
  /** How many people are ahead of them, so waiting has some shape to it. */
  position?: number;
}
